<template>
  <div class="ai-triage-rules-workspace">
    <a-alert
      v-if="storageScopeIsDomain && !hideStorageScopeNotice"
      type="info"
      show-icon
      class="mb-3"
      message="Shared for this host"
    >
      <template slot="description">
        These prompt versions are shared for this host (storage key: <code class="mono">{{ effectiveStorageKey }}</code>).
      </template>
    </a-alert>

    <a-card v-if="preferencesSectionEnabled" class="mb-3 triage-policy-card" title="AiTriage preferences" size="small">
      <div class="policy-legend">
        <p class="policy-legend__intro">
          For each item, choose <strong>Low</strong>, <strong>Medium</strong>, or <strong>High</strong>.
        </p>
        <ul class="policy-legend__list">
          <li class="policy-legend__item">
            <span class="policy-legend__badge">
              <a-tag color="red" class="policy-legend__tag">override</a-tag>
            </span>
            <span class="policy-legend__text">
              <strong>Low</strong>, <strong>Medium</strong>, and <strong>High</strong> are always shown; you always control the level.
            </span>
          </li>
          <li class="policy-legend__item policy-legend__item--suggested">
            <span class="policy-legend__badge">
              <a-tag class="policy-legend__tag ai-triage-badge--suggested">suggested</a-tag>
            </span>
            <span class="policy-legend__text">
              AI Triage uses <strong>stricter</strong> rules based on the saved level.
            </span>
          </li>
        </ul>
      </div>

      <a-empty
        v-if="!configurableItems.length"
        description="No configurable items: backend returned an empty list."
      />

      <div v-else>
        <a-card
          v-for="cfg in configurableItems"
          :key="cfg.id"
          size="small"
          class="mb-2 config-item-card"
        >
          <template slot="title">
            <span class="config-item-card__title-row">
              <span class="config-item-card__title-text">{{ cfg.label }}</span>
              <a-tag
                v-if="cfg.badge"
                :color="cfg.badge === 'override' ? 'red' : undefined"
                :class="[
                  'config-item-card__kind-tag',
                  cfg.badge === 'suggested' ? 'ai-triage-badge--suggested' : '',
                ]"
              >
                {{ cfg.badge }}
              </a-tag>
            </span>
          </template>
          <template slot="extra">
            <a-tag :color="tagColorForLevel(levelForConfigItem(cfg))">
              {{ levelForConfigItem(cfg) | uppercase }}
            </a-tag>
          </template>
          <p v-if="cfg.description" class="config-item-desc">{{ cfg.description }}</p>
          <div class="level-picker">
            <span class="level-picker-label">Level</span>
            <a-radio-group v-model="itemLevels[cfg.id]">
              <a-radio-button
                v-for="opt in levelOptions"
                :key="opt.value"
                :value="opt.value"
              >
                {{ opt.label }}
              </a-radio-button>
            </a-radio-group>
          </div>
        </a-card>

        <div class="d-flex flex-wrap align-items-center mt-3" style="gap: 12px">
          <a-button type="primary" :loading="isSavingPreferences || savingPreferences" @click="savePreferencesToServer">
            Save preferences
          </a-button>
          <span class="fs-12 text-muted">
            Update your preferences and click Save.
          </span>
        </div>
      </div>
    </a-card>

    <a-collapse
      v-if="showAdvancedPromptSection"
      v-model="advancedPromptCollapseKeys"
      expand-icon-position="left"
      :bordered="false"
      class="mb-3 ai-triage-advanced-collapse"
    >
      <a-collapse-panel key="advanced-prompt">
        <template slot="header">
          <span class="ai-triage-advanced-collapse__title">
            <em class="ai-triage-advanced-collapse__quote">For advanced users only</em>
            <span class="text-muted fs-12 ai-triage-advanced-collapse__subtitle">
              Custom prompt text, limits, and version history — click to expand or collapse.
            </span>
          </span>
        </template>

        <a-alert
          type="warning"
          show-icon
          class="mb-3"
        >
          <template slot="description">
            <div>
              Characters: <strong>{{ editorContent.length }}</strong> / {{ maxChars }}
            </div>
            <div v-if="promptLengthExceeded" class="mt-1" style="color: #d4380d">
              Shorten your prompt to stay within the character limit before saving.
            </div>
            <div class="mt-2">
              <strong>Retention:</strong> only the <strong>last {{ maxVersions }}</strong> prompt versions are kept (here and on the server);
              older ones drop when you save a new version.
            </div>
            <div class="mt-2">
              <strong>Use at your own risk:</strong> vague, incorrect, or unsuitable prompts can worsen triage quality or alerting behaviour.
              You are responsible for what you save, there is no guarantee that a custom prompt will produce desirable results.
            </div>
            <div class="mt-2 fs-12 text-muted">
              Draft and versions are cached in this browser until you use <strong>Save prompt</strong>.
            </div>
          </template>
        </a-alert>

        <a-row :gutter="16" type="flex" class="workspace-row">
      <!-- Left: versions — equal-height cards, full text + scroll + copy -->
      <a-col :xs="24" :md="12" class="prompt-versions-col">
        <div class="panel-title">Prompt versions</div>
        <p class="panel-subtitle">
          Select text to copy, or use Copy. Click a card to load it into the editor.
        </p>
        <div class="versions-stack">
          <div v-if="!versions.length" class="empty-versions">
            No saved versions yet. Save from the editor.
          </div>
          <div
            v-for="(item, index) in versions"
            :key="item.id"
            class="version-card"
            :class="{
              'version-card--active': item.isActive,
              'version-card--selected': item.id === selectedVersionId,
            }"
            @click="selectVersion(item)"
          >
            <div class="version-card__header">
              <span class="version-card__title">{{ formatVersionTitle(item, index) }}</span>
              <a-tag v-if="item.isActive" color="green" class="ml-1">Active</a-tag>
              <a-button
                type="link"
                size="small"
                class="version-card__copy"
                @click.stop="copyVersionContent(item)"
              >
                <a-icon type="copy" />
                Copy
              </a-button>
            </div>
            <div class="version-card__body">
              <textarea
                readonly
                class="version-card__textarea"
                :value="item.content"
                spellcheck="false"
              />
            </div>
          </div>
        </div>
      </a-col>

      <!-- Center: editor -->
      <a-col :xs="24" :md="12" class="prompt-editor-col">
        <div class="panel-title">Editor</div>
        <a-textarea
          v-model="editorContent"
          :rows="16"
          placeholder="Enter or edit the triage prompt…"
          class="prompt-textarea flex-grow-editor"
        />
        <div class="mt-2 d-flex flex-wrap" style="gap: 8px">
          <a-button
            type="primary"
            :loading="isSavingPrompt || savingPrompt"
            :disabled="promptLengthExceeded"
            @click="savePromptAsLatestAndSync"
          >
            Save prompt
          </a-button>
        </div>
        <p class="hint-text mt-2">
          <strong>Save prompt</strong> adds a new version from the editor, marks it active, and syncs it to the server as the latest prompt.
        </p>
      </a-col>

      <!--
      Right: sample output preview (disabled)
      <a-col :xs="24" :md="8" class="prompt-preview-col">
        <div class="panel-title">Preview (sample output)</div>
        <div class="preview-box">
          <pre class="preview-pre">{{ samplePreviewText }}</pre>
        </div>
        <p class="hint-text mt-2">
          Placeholder preview from the current editor text. Wire this to your model when the API is ready.
        </p>
      </a-col>
      -->
        </a-row>
      </a-collapse-panel>
    </a-collapse>

  </div>
</template>

<script>
import { debounce } from 'lodash';
import {
  AI_TRIAGE_CONFIG_ITEMS_EXAMPLE,
  AI_TRIAGE_LEVEL_OPTIONS,
} from '../consts/aiTriage.consts';

const STORAGE_PREFIX = 'ai_triage_rules_v3_';
const LEGACY_STORAGE_PREFIX = 'llm_prompt_rules_v3_';
const ALLOWED_LEVELS = new Set(['LOW', 'MEDIUM', 'HIGH']);
const DEFAULT_LEVEL_BY_ID = (AI_TRIAGE_CONFIG_ITEMS_EXAMPLE || []).reduce((acc, item) => {
  if (!item || !item.id || typeof item.default_level !== 'string') {
    return acc;
  }
  const level = item.default_level.toUpperCase();
  if (ALLOWED_LEVELS.has(level)) {
    acc[item.id] = level;
  }
  return acc;
}, {});

function advancedPromptAllowedFromPayload(t) {
  if (!t || typeof t !== 'object') {
    return true;
  }
  if (!Object.prototype.hasOwnProperty.call(t, 'advanced_prompt_allowed')) {
    return true;
  }
  const v = t.advanced_prompt_allowed;
  return v === true || v === 1 || v === '1';
}

export default {
  name: 'AiTriageRulesWorkspace',
  props: {
    urlId: {
      type: [Number, String],
      default: null,
    },
    fqdnId: {
      type: [Number, String],
      default: null,
    },
    storageScopeKey: {
      type: String,
      default: '',
    },
    storageScopeIsDomain: {
      type: Boolean,
      default: false,
    },
    /**
     * Dimensions from backend (GET). Each: { id: string, label: string, description?: string }.
     * `id` must match DB dimension key; stable across releases.
     */
    configurableItems: {
      type: Array,
      default: () => [],
    },
    serverAiTriage: {
      type: Object,
      default: null,
    },
    /** Parent sets true while PUT preferences is in flight. */
    savingPreferences: {
      type: Boolean,
      default: false,
    },
    /** Parent sets true while PUT prompt is in flight. */
    savingPrompt: {
      type: Boolean,
      default: false,
    },
    advancedPromptUiEnabled: {
      type: Boolean,
      default: true,
    },
    preferencesSectionEnabled: {
      type: Boolean,
      default: true,
    },
    promptSectionEnabled: {
      type: Boolean,
      default: false,
    },
    hideStorageScopeNotice: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      levelOptions: AI_TRIAGE_LEVEL_OPTIONS,
      itemLevels: {},
      lastAppliedAiTriageScopeKey: null,
      isSavingPreferences: false,
      isSavingPrompt: false,
      maxVersions: 3,
      maxChars: 3000,
      versions: [],
      editorContent: '',
      selectedVersionId: null,
      hasCachedDraftContent: false,
      advancedPromptCollapseKeys: [],
    };
  },
  computed: {
    triageContextKey() {
      if (this.fqdnId != null && this.fqdnId !== '') {
        return `fqdn-${this.fqdnId}`;
      }
      if (this.urlId != null && this.urlId !== '') {
        return `url-${this.urlId}`;
      }
      return '';
    },
    resolvedScopeSegment() {
      const s = (this.storageScopeKey || '').trim();
      if (s) {
        return s;
      }
      if (this.fqdnId != null && this.fqdnId !== '') {
        return `fqdn-${this.fqdnId}`;
      }
      return `url-${this.urlId}`;
    },
    effectiveStorageKey() {
      return `${STORAGE_PREFIX}${this.resolvedScopeSegment}`;
    },
    promptLengthExceeded() {
      return (this.editorContent || '').length > this.maxChars;
    },
    advancedPromptAllowed() {
      return advancedPromptAllowedFromPayload(this.serverAiTriage);
    },
    showAdvancedPromptSection() {
      return (
        this.promptSectionEnabled &&
        this.advancedPromptAllowed &&
        this.advancedPromptUiEnabled
      );
    },
    samplePreviewText() {
      const raw = (this.editorContent || '').trim();
      const levelSummary = (this.configurableItems || [])
        .map((c) => `${c.id}=${this.levelForConfigItem(c)}`)
        .join(', ');
      const policyLines = [
        levelSummary ? `Item levels: ${levelSummary}` : 'Item levels: (none)',
      ];
      if (!raw) {
        return ['--- Sample triage output ---', ...policyLines, '', '[No extra user prompt — add text in the editor below.]'].join('\n');
      }
      return [
        '--- Sample triage output ---',
        `Prompt length: ${raw.length} characters`,
        '',
        'Summary: (mock) Model would apply the above instructions to the latest baseline HTML.',
        '',
        `Instructions preview:\n${raw.slice(0, 400)}${raw.length > 400 ? '…' : ''}`,
      ].join('\n');
    },
  },
  created() {
    this.persistDraftDebounced = debounce(() => this.persist(), 400);
  },
  watch: {
    triageContextKey: {
      immediate: true,
      handler() {
        this.lastAppliedAiTriageScopeKey = null;
        this.loadFromStorage();
      },
    },
    storageScopeKey() {
      this.lastAppliedAiTriageScopeKey = null;
      this.loadFromStorage();
      this.$nextTick(() => {
        if (this.serverAiTriage && typeof this.serverAiTriage === 'object') {
          this.lastAppliedAiTriageScopeKey = null;
          this.applyFromServer(this.serverAiTriage);
          this.lastAppliedAiTriageScopeKey = this.resolvedScopeSegment;
        }
      });
    },
    serverAiTriage: {
      immediate: true,
      deep: true,
      handler(val) {
        if (!val || typeof val !== 'object') {
          return;
        }
        if (this.lastAppliedAiTriageScopeKey === this.resolvedScopeSegment) {
          return;
        }
        this.lastAppliedAiTriageScopeKey = this.resolvedScopeSegment;
        this.applyFromServer(val);
      },
    },
    configurableItems: {
      immediate: true,
      deep: true,
      handler() {
        this.ensureItemLevelsForList(this.configurableItems);
      },
    },
    versions: {
      deep: true,
      handler() {
        this.persist();
      },
    },
    editorContent() {
      if (this.persistDraftDebounced) {
        this.persistDraftDebounced();
      }
    },
    itemLevels: {
      deep: true,
      handler() {
        if (this.persistDraftDebounced) {
          this.persistDraftDebounced();
        }
      },
    },
  },
  methods: {
    storageKey() {
      return this.effectiveStorageKey;
    },
    applyFromServer(t) {
      const levels = t.saved_item_levels;
      if (levels && typeof levels === 'object') {
        this.applyPolicyFromParsed({ itemLevels: levels });
      } else {
        this.resetPolicyToDefaults();
      }
      if (!advancedPromptAllowedFromPayload(t)) {
        this.versions = [];
        this.editorContent = '';
        this.selectedVersionId = null;
        this.hasCachedDraftContent = false;
        this.advancedPromptCollapseKeys = [];
        this.$nextTick(() => {
          this.persist();
        });
        return;
      }
      const hasServerDraftField = Object.prototype.hasOwnProperty.call(t, 'draft_content');
      const draft = typeof t.draft_content === 'string' ? t.draft_content : '';
      let versions = Array.isArray(t.versions) ? t.versions.map((v) => ({ ...v })) : [];
      if (!versions.length && hasServerDraftField) {
        versions = [
          {
            id: `v_sync_${Date.now()}`,
            content: draft,
            createdAt: new Date().toISOString(),
            isActive: true,
            serverVersionId: null,
          },
        ];
      }
      if (!this.hasCachedDraftContent) {
        this.versions = versions;
        this.editorContent = draft;
        const active = this.versions.find((v) => v.isActive);
        this.selectedVersionId = active
          ? active.id
          : (this.versions[0] && this.versions[0].id) || null;
      }
      this.$nextTick(() => {
        this.persist();
      });
    },
    loadFromStorage() {
      try {
        let raw = window.localStorage.getItem(this.storageKey());
        const legacyKey = `${LEGACY_STORAGE_PREFIX}${this.resolvedScopeSegment}`;
        let loadedFromLegacy = false;
        if (!raw) {
          raw = window.localStorage.getItem(legacyKey);
          loadedFromLegacy = !!raw;
        }
        if (!raw) {
          this.resetPolicyToDefaults();
          this.versions = [];
          this.editorContent = '';
          this.selectedVersionId = null;
          this.hasCachedDraftContent = false;
          return;
        }
        const parsed = JSON.parse(raw);
        this.applyPolicyFromParsed(parsed.policy);
        this.versions = Array.isArray(parsed.versions) ? parsed.versions : [];
        this.editorContent = parsed.draftContent != null ? parsed.draftContent : '';
        this.hasCachedDraftContent = Object.prototype.hasOwnProperty.call(parsed, 'draftContent');
        const active = this.versions.find((v) => v.isActive);
        this.selectedVersionId = active ? active.id : (this.versions[0] && this.versions[0].id) || null;
        if (loadedFromLegacy) {
          this.$nextTick(() => {
            this.persist();
            window.localStorage.removeItem(legacyKey);
          });
        }
      } catch (e) {
        this.resetPolicyToDefaults();
        this.versions = [];
        this.editorContent = '';
        this.selectedVersionId = null;
        this.hasCachedDraftContent = false;
      }
    },
    resetPolicyToDefaults() {
      this.itemLevels = {};
      this.ensureItemLevelsForList(this.configurableItems);
    },
    defaultLevelForConfigItem(cfg) {
      if (!cfg || !cfg.id) {
        return 'MEDIUM';
      }

      const fromConst = DEFAULT_LEVEL_BY_ID[cfg.id];
      if (fromConst) return fromConst;

      const fromConfigItem = typeof cfg.default_level === 'string'
        ? cfg.default_level.toUpperCase()
        : '';
      if (ALLOWED_LEVELS.has(fromConfigItem)) return fromConfigItem;

      return 'MEDIUM';
    },
    levelForConfigItem(cfg) {
      if (!cfg || !cfg.id) {
        return 'MEDIUM';
      }

      return this.itemLevels[cfg.id] || this.defaultLevelForConfigItem(cfg);
    },
    ensureItemLevelsForList(items) {
      const list = Array.isArray(items) ? items : [];
      list.forEach((cfg) => {
        if (!cfg || !cfg.id) return;
        if (this.itemLevels[cfg.id] == null) {
          this.$set(this.itemLevels, cfg.id, this.defaultLevelForConfigItem(cfg));
        }
      });
      Object.keys(this.itemLevels).forEach((key) => {
        if (!list.some((c) => c && c.id === key)) {
          this.$delete(this.itemLevels, key);
        }
      });
    },
    applyPolicyFromParsed(policy) {
      const levels = policy && policy.itemLevels;
      if (levels && typeof levels === 'object') {
        Object.keys(levels).forEach((id) => {
          const v = levels[id];
          if (typeof v === 'string' && ALLOWED_LEVELS.has(v)) {
            this.$set(this.itemLevels, id, v);
          }
        });
      }
      this.ensureItemLevelsForList(this.configurableItems);
    },
    policyPayload() {
      const itemLevels = {};
      (this.configurableItems || []).forEach((cfg) => {
        if (!cfg || !cfg.id) return;
        const fallback = this.defaultLevelForConfigItem(cfg);
        itemLevels[cfg.id] = this.itemLevels[cfg.id] || fallback;
      });
      return {
        itemLevels,
        configSchemaVersion: 1,
      };
    },
    tagColorForLevel(level) {
      const l = level || 'MEDIUM';
      if (l === 'LOW') return 'blue';
      if (l === 'HIGH') return 'red';
      return 'cyan';
    },
    async savePreferencesToServer() {
      if (this.triageContextKey === '') {
        this.$message.warning('Missing page or host for saving.');
        return;
      }
      this.isSavingPreferences = true;
      try {
        this.persist();
        const policy = this.policyPayload();
        this.$emit('save-preferences', {
          urlId: this.urlId,
          fqdnId: this.fqdnId,
          storageScopeKey: this.resolvedScopeSegment,
          storageScopeIsDomain: this.storageScopeIsDomain,
          itemLevels: policy.itemLevels,
        });
      } finally {
        this.isSavingPreferences = false;
      }
    },
    async savePromptAsLatestAndSync() {
      if (this.triageContextKey === '') {
        this.$message.warning('Missing page or host for saving.');
        return;
      }
      if (this.promptLengthExceeded) {
        this.$message.warning('Shorten your prompt to stay within the character limit.');
        return;
      }

      const id = `v_${Date.now()}`;
      const entry = {
        id,
        content: this.editorContent,
        createdAt: new Date().toISOString(),
        isActive: false,
        serverVersionId: null,
      };

      const combined = [entry, ...this.versions];
      this.versions = combined.slice(0, this.maxVersions).map((v, i) => ({
        ...v,
        isActive: i === 0,
      }));
      this.selectedVersionId = id;

      this.isSavingPrompt = true;
      try {
        this.hasCachedDraftContent = true;
        this.persist();
        this.$emit('save-prompt', {
          urlId: this.urlId,
          fqdnId: this.fqdnId,
          storageScopeKey: this.resolvedScopeSegment,
          storageScopeIsDomain: this.storageScopeIsDomain,
          draft_content: this.editorContent,
          versions: this.versions.map((v) => ({ ...v })),
        });
      } finally {
        this.isSavingPrompt = false;
      }
    },
    persist() {
      try {
        const promptAllowed = this.advancedPromptAllowed;
        window.localStorage.setItem(
          this.storageKey(),
          JSON.stringify({
            policy: this.policyPayload(),
            versions: promptAllowed ? this.versions : [],
            draftContent: promptAllowed ? this.editorContent : '',
          })
        );
      } catch (e) {
        console.warn('AiTriageRulesWorkspace: persist failed', e);
      }
    },
    async copyVersionContent(item) {
      const text = (item && item.content) || '';
      try {
        await navigator.clipboard.writeText(text);
        this.$message.success('Copied to clipboard');
      } catch (e) {
        this.$message.error('Copy failed — select text in the box and copy manually');
      }
    },
    formatVersionTitle(item, index) {
      const fallback = `Version ${index + 1}`;
      if (!item.createdAt) return fallback;
      try {
        const d = new Date(item.createdAt);
        return d.toLocaleString();
      } catch (e) {
        return fallback;
      }
    },
    selectVersion(item) {
      this.selectedVersionId = item.id;
      this.editorContent = item.content || '';
    },
  },
};
</script>

<style lang="scss" scoped>
.ai-triage-rules-workspace {
  .ai-triage-advanced-collapse {
    background: #fafafa;
    border: 1px solid #f0f0f0;
    border-radius: 6px;
    padding: 0 12px;
  }
  .ai-triage-advanced-collapse__title {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 8px;
  }
  .ai-triage-advanced-collapse__quote {
    font-style: italic;
    color: #434343;
    font-weight: 600;
  }
  .ai-triage-advanced-collapse__subtitle {
    font-weight: normal;
  }

  .triage-policy-card {
    border-color: #d9d9d9;
  }
  .policy-lead {
    font-size: 13px;
    color: #595959;
    margin-bottom: 12px;
    line-height: 1.5;
  }
  .policy-legend {
    background: #fafafa;
    border: 1px solid #f0f0f0;
    border-radius: 8px;
    padding: 12px 14px;
    margin-bottom: 16px;
  }
  .policy-legend__intro {
    font-size: 13px;
    color: #595959;
    line-height: 1.5;
    margin-bottom: 12px;
  }
  .policy-legend__list {
    list-style: none;
    padding: 0;
    margin: 0;
  }
  .policy-legend__item {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    margin-bottom: 10px;
    &:last-child {
      margin-bottom: 0;
    }
  }
  .policy-legend__badge {
    flex-shrink: 0;
    padding-top: 1px;
  }
  .policy-legend__tag {
    margin: 0;
    border-radius: 4px;
    font-weight: 600;
    letter-spacing: 0.02em;
    text-transform: lowercase;
  }
  /** Light blue chip for suggested (override uses Ant Design `red` preset). */
  .ai-triage-badge--suggested {
    background: #e6f7ff !important;
    border-color: #91d5ff !important;
    color: #1890ff !important;
  }
  .policy-legend__text {
    flex: 1;
    font-size: 13px;
    color: #595959;
    line-height: 1.55;
    min-width: 0;
  }
  .policy-legend__item--suggested .policy-legend__text {
    color: #434343;
  }
  .config-item-card {
    border-color: #e8e8e8;

    ::v-deep .ant-card-head-title {
      padding-bottom: 0;
    }
  }
  .config-item-card__title-row {
    display: inline-flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    max-width: 100%;
  }
  .config-item-card__title-text {
    font-weight: 600;
    color: #323232;
  }
  .config-item-card__kind-tag {
    margin: 0 !important;
    font-weight: 600;
    text-transform: lowercase;
    letter-spacing: 0.02em;
  }
  .config-item-desc {
    font-size: 13px;
    color: #595959;
    margin-bottom: 12px;
    line-height: 1.5;
  }
  .level-picker {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px;
  }
  .level-picker-label {
    font-size: 13px;
    font-weight: 600;
    color: #323232;
    min-width: 48px;
  }
  .text-muted {
    color: #888;
  }
  .fs-11 {
    font-size: 11px;
  }
  .fs-12 {
    font-size: 12px;
  }
  .mono {
    font-family: 'Roboto Mono', Menlo, Monaco, Consolas, monospace;
  }
  .panel-title {
    font-weight: 600;
    margin-bottom: 4px;
    color: #323232;
  }
  .panel-subtitle {
    font-size: 12px;
    color: #888;
    margin-bottom: 10px;
    line-height: 1.4;
  }

  /* Row fills viewport under the alert — big vertical space for all columns */
  .workspace-row {
    min-height: calc(100vh - 280px);
    align-items: stretch;
  }

  .prompt-versions-col,
  .prompt-editor-col,
  .prompt-preview-col {
    display: flex;
    flex-direction: column;
    min-height: 0;
  }

  .versions-stack {
    flex: 1 1 auto;
    min-height: 360px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    overflow: hidden;
  }

  /* Each version uses equal share of the stack (max 3) */
  .version-card {
    flex: 1 1 0;
    min-height: 0;
    display: flex;
    flex-direction: column;
    border: 1px solid #e8e8e8;
    border-radius: 4px;
    background: #fff;
    cursor: pointer;
    overflow: hidden;
    transition: box-shadow 0.15s;
    &:hover {
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    }
  }
  .version-card--active {
    border-color: #b7eb8f;
    background: #fcfffa;
  }
  .version-card--selected:not(.version-card--active) {
    border-color: #91d5ff;
    box-shadow: 0 0 0 1px #91d5ff;
  }

  .version-card__header {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px;
    padding: 8px 10px 4px;
    border-bottom: 1px solid #f0f0f0;
    flex-shrink: 0;
  }
  .version-card__title {
    font-size: 12px;
    font-weight: 600;
    flex: 1;
    min-width: 0;
  }
  .version-card__copy {
    padding: 0 4px;
    height: auto;
    font-size: 12px;
  }
  .version-card__meta {
    padding: 2px 10px 6px;
    display: flex;
    justify-content: space-between;
    gap: 8px;
    flex-shrink: 0;
  }

  .version-card__body {
    flex: 1 1 auto;
    min-height: 0;
    position: relative;
    padding: 0;
  }
  .version-card__textarea {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    margin: 0;
    padding: 10px;
    border: none;
    resize: none;
    font-size: 12px;
    line-height: 1.45;
    font-family: 'Roboto Mono', Menlo, Monaco, Consolas, monospace;
    color: #333;
    background: #fafafa;
    box-sizing: border-box;
    overflow-y: auto;
    cursor: text;
    &:focus {
      outline: none;
      background: #fff;
    }
  }

  .empty-versions {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    text-align: center;
    color: #888;
    font-size: 13px;
    border: 1px dashed #d9d9d9;
    border-radius: 4px;
  }

  .prompt-textarea {
    font-family: 'Roboto Mono', Menlo, Monaco, Consolas, monospace;
    font-size: 13px;
  }
  .flex-grow-editor {
    flex: 1 1 auto;
    min-height: 280px;
  }
  .prompt-editor-col .ant-input {
    height: 100%;
  }

  .preview-box {
    flex: 1 1 auto;
    min-height: 200px;
    border: 1px solid #e8e8e8;
    border-radius: 4px;
    background: #fafafa;
    padding: 12px;
    overflow: auto;
  }
  .preview-pre {
    margin: 0;
    white-space: pre-wrap;
    word-break: break-word;
    font-size: 12px;
    font-family: 'Roboto Mono', Menlo, Monaco, Consolas, monospace;
  }
  .hint-text {
    font-size: 12px;
    color: #888;
    flex-shrink: 0;
  }
}
</style>
